// Music Player module - функциональный музыкальный плеер для модального окна
export default class MusicPlayer {
  constructor(element) {
    this.element = element;
    this.audio = null;
    this.isPlaying = false;
    this.currentVolume = 0.7; // Начальная громкость 70%
    this.musicUrl = null;
    this.musicianId = null;

    // Элементы плеера
    this.playPauseBtn = element.querySelector('.music-modal__player-pause');
    this.progressBar = element.querySelector('.music-modal__player-progress-bar');
    this.progressContainer = element.querySelector('.music-modal__player-progress');
    this.volumeBtn = element.querySelector('.music-modal__player-volume');
    this.volumeSlider = element.querySelector('.music-modal__player-volume-slider');
    this.volumeIcon = this.volumeBtn?.querySelector('i');

    this.init();
  }

  init() {
    // Получаем ID музыканта из data-атрибута модального окна
    const modal = this.element.closest('.modal');
    if (modal) {
      this.musicianId = modal.dataset.musicianId || null;
    }

    // Инициализация элементов
    if (this.playPauseBtn) {
      this.playPauseBtn.addEventListener('click', () => this.togglePlayPause());
    }

    if (this.progressContainer) {
      this.progressContainer.addEventListener('click', (e) => this.seek(e));
    }

    if (this.volumeSlider) {
      this.volumeSlider.addEventListener('input', (e) => this.setVolume(e.target.value / 100));
      this.volumeSlider.value = this.currentVolume * 100;
    }

    if (this.volumeBtn) {
      this.volumeBtn.addEventListener('click', () => this.toggleMute());
    }

    // Загружаем сохраненную громкость
    this.loadSavedVolume();
    
    // Обновляем иконку громкости
    this.updateVolumeIcon();

    // Загружаем музыку при открытии модального окна
    if (this.musicianId) {
      this.loadMusic();
    }

    // Обработка событий модального окна Bootstrap
    if (modal) {
      modal.addEventListener('shown.bs.modal', () => {
        if (this.musicianId && !this.audio) {
          this.loadMusic();
        }
      });

      modal.addEventListener('hidden.bs.modal', () => {
        this.pause();
      });
    }
  }

  // Загрузка музыки с сервера
  async loadMusic() {
    if (!this.musicianId) {
      console.warn('MusicPlayer: musicianId не указан');
      return;
    }

    try {
      // GET запрос к серверу для получения ссылки на музыку
      const response = await fetch(`/api/musicians/${this.musicianId}/music`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.musicUrl = data.musicUrl || data.url || null;

      if (!this.musicUrl) {
        console.warn('MusicPlayer: ссылка на музыку не получена');
        return;
      }

      // Инициализация аудио
      this.initAudio(this.musicUrl);
    } catch (error) {
      console.error('MusicPlayer: ошибка загрузки музыки:', error);
      // Fallback: можно использовать заглушку или показать сообщение об ошибке
    }
  }

  // Инициализация HTML5 Audio
  initAudio(url) {
    if (this.audio) {
      this.audio.pause();
      this.audio = null;
    }

    this.audio = new Audio(url);
    this.audio.volume = this.currentVolume;
    this.audio.preload = 'metadata';
    
    // Обновляем иконку громкости после создания аудио
    this.updateVolumeIcon();

    // Обработчики событий аудио
    this.audio.addEventListener('loadedmetadata', () => {
      this.updateProgress();
    });

    this.audio.addEventListener('timeupdate', () => {
      this.updateProgress();
    });

    this.audio.addEventListener('ended', () => {
      this.isPlaying = false;
      this.updatePlayPauseButton();
      if (this.progressBar) {
        this.progressBar.style.width = '0%';
      }
    });

    this.audio.addEventListener('error', (e) => {
      console.error('MusicPlayer: ошибка воспроизведения:', e);
      this.isPlaying = false;
      this.updatePlayPauseButton();
    });

    this.audio.addEventListener('play', () => {
      this.isPlaying = true;
      this.updatePlayPauseButton();
    });

    this.audio.addEventListener('pause', () => {
      this.isPlaying = false;
      this.updatePlayPauseButton();
    });
  }

  // Переключение play/pause
  togglePlayPause() {
    if (!this.audio) {
      if (this.musicUrl) {
        this.initAudio(this.musicUrl);
      } else if (this.musicianId) {
        this.loadMusic().then(() => {
          if (this.audio) {
            this.play();
          }
        });
      }
      return;
    }

    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  // Воспроизведение
  play() {
    if (this.audio) {
      this.audio.play().catch(error => {
        console.error('MusicPlayer: ошибка воспроизведения:', error);
      });
    }
  }

  // Пауза
  pause() {
    if (this.audio) {
      this.audio.pause();
    }
  }

  // Обновление кнопки play/pause
  updatePlayPauseButton() {
    if (!this.playPauseBtn) return;

    const icon = this.playPauseBtn.querySelector('i');
    if (icon) {
      if (this.isPlaying) {
        icon.classList.remove('fa-play');
        icon.classList.add('fa-pause');
        this.playPauseBtn.setAttribute('aria-label', 'Pause');
      } else {
        icon.classList.remove('fa-pause');
        icon.classList.add('fa-play');
        this.playPauseBtn.setAttribute('aria-label', 'Play');
      }
    }
  }

  // Установка громкости (0-1)
  setVolume(volume) {
    this.currentVolume = Math.max(0, Math.min(1, volume));
    
    if (this.audio) {
      this.audio.volume = this.currentVolume;
    }

    // Обновление иконки громкости
    this.updateVolumeIcon();

    // Сохранение громкости в localStorage
    localStorage.setItem('musicPlayerVolume', this.currentVolume.toString());
  }

  // Переключение mute/unmute
  toggleMute() {
    if (!this.audio) return;

    if (this.audio.volume > 0) {
      // Сохраняем текущую громкость и устанавливаем 0
      this.audio.dataset.previousVolume = this.audio.volume.toString();
      this.setVolume(0);
      if (this.volumeSlider) {
        this.volumeSlider.value = 0;
      }
    } else {
      // Восстанавливаем предыдущую громкость
      const previousVolume = parseFloat(this.audio.dataset.previousVolume || this.currentVolume);
      this.setVolume(previousVolume);
      if (this.volumeSlider) {
        this.volumeSlider.value = previousVolume * 100;
      }
    }
  }

  // Обновление иконки громкости
  updateVolumeIcon() {
    if (!this.volumeIcon) return;

    const volume = this.audio ? this.audio.volume : this.currentVolume;

    // Удаляем все классы иконок громкости
    this.volumeIcon.classList.remove('fa-volume-up', 'fa-volume-down', 'fa-volume-off', 'fa-volume-mute');

    if (volume === 0) {
      this.volumeIcon.classList.add('fa-volume-mute');
    } else if (volume < 0.5) {
      this.volumeIcon.classList.add('fa-volume-down');
    } else {
      this.volumeIcon.classList.add('fa-volume-up');
    }
  }

  // Обновление прогресс-бара
  updateProgress() {
    if (!this.audio || !this.progressBar) return;

    const currentTime = this.audio.currentTime;
    const duration = this.audio.duration;

    if (duration && !isNaN(duration)) {
      const progress = (currentTime / duration) * 100;
      this.progressBar.style.width = `${progress}%`;
    }
  }

  // Переход к определенной позиции (seek)
  seek(event) {
    if (!this.audio || !this.progressContainer) return;

    const rect = this.progressContainer.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const width = rect.width;
    const percentage = clickX / width;

    if (this.audio.duration && !isNaN(this.audio.duration)) {
      this.audio.currentTime = percentage * this.audio.duration;
    }
  }

  // Загрузка сохраненной громкости из localStorage
  loadSavedVolume() {
    const savedVolume = localStorage.getItem('musicPlayerVolume');
    if (savedVolume) {
      this.currentVolume = parseFloat(savedVolume);
      if (this.volumeSlider) {
        this.volumeSlider.value = this.currentVolume * 100;
      }
    }
  }

  // Очистка ресурсов
  destroy() {
    if (this.audio) {
      this.audio.pause();
      this.audio = null;
    }
    this.isPlaying = false;
  }
}

